import {
  FC,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react'

import { Button, ButtonVariants } from '@inno-ui/button'
import { MultiplyIcon } from '@inno-ui/icons'
import { Modal } from '@inno-ui/modal'
import { CustomEvents } from 'shared/constants/customEvents'
import { useMediaQuery } from 'shared/hooks/useMediaQuery'
import { useUserContext } from 'shared/hooks/useUserContext'

import styles from './ProductTourModal.module.scss'
import { TourPage } from './ProductTourModal.types'
import { AllApps } from './pages/AllApps'
import { Homepage } from './pages/Homepage'
import { InterconnectedApps } from './pages/InterconnectedApps'
import { Shortcuts } from './pages/Shortcuts'

export const ProductTourModal: FC = () => {
  const { user } = useUserContext()
  const { isMobile } = useMediaQuery()
  const [activePage, setActivePage] = useState<TourPage>('homepage')
  const [isOpen, setIsOpen] = useState(false)
  const modalContentRef = useRef<HTMLDivElement>(null)

  const handleOpenProductTour = () => {
    setIsOpen(true)
  }
  const handleOpenProductTourAfterOnboarding = useCallback(() => {
    if (user && user.container_user_tour_completed && !isMobile) {
      setIsOpen(true)
    }
  }, [isMobile, user])

  useEffect(() => {
    document.addEventListener(
      CustomEvents.OPEN_PRODUCT_TOUR,
      handleOpenProductTour
    )
    document.addEventListener(
      CustomEvents.ONBOARDING_TOUR_COMPLETED,
      handleOpenProductTourAfterOnboarding
    )

    return () => {
      document.removeEventListener(
        CustomEvents.OPEN_PRODUCT_TOUR,
        handleOpenProductTour
      )
      document.removeEventListener(
        CustomEvents.ONBOARDING_TOUR_COMPLETED,
        handleOpenProductTourAfterOnboarding
      )
    }
  }, [user, isMobile, handleOpenProductTourAfterOnboarding])

  const handleCloseTour = () => {
    setIsOpen(false)
  }

  const renderActivePage = () => {
    switch (activePage) {
      case 'homepage':
      default:
        return (
          <Homepage setActivePage={setActivePage} skipTour={handleCloseTour} />
        )
      case 'interconnected-apps':
        return <InterconnectedApps setActivePage={setActivePage} />
      case 'all-apps':
        return <AllApps setActivePage={setActivePage} />
      case 'shortcuts':
        return (
          <Shortcuts
            setActivePage={setActivePage}
            completeTour={handleCloseTour}
          />
        )
    }
  }

  useEffect(() => {
    if (!isOpen) {
      setActivePage('homepage')
    }
  }, [isOpen])

  useLayoutEffect(() => {
    if (modalContentRef.current) {
      modalContentRef.current.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }, [activePage])

  return (
    <Modal
      isOpen={isOpen}
      maxWidth={'75rem'}
      maxHeight={800}
      onClose={handleCloseTour}
      withHeaderLine={false}
      zIndex={100}
      layers={[
        {
          showHeader: false,
          bodyPaddings: false,
          body: (
            <div ref={modalContentRef} className={styles.productTourModal}>
              <div className={styles.closeButton}>
                <Button
                  variant={ButtonVariants.Ghost}
                  onClick={handleCloseTour}
                  icon={MultiplyIcon}
                />
              </div>
              {renderActivePage()}
            </div>
          )
        }
      ]}
    />
  )
}
